function isEmptyTarget(target) {
    return target === '';
}
function isAbsoluteOrProtocolRelative(target) {
    return !target.startsWith('/') || target.startsWith('//');
}
function isSameOrigin(target) {
    try {
        const resolved = new URL(target, window.location.origin);
        return resolved.origin === window.location.origin;
    }
    catch {
        return false;
    }
}
function parseAbsoluteUrl(url) {
    try {
        return new URL(url);
    }
    catch {
        return undefined;
    }
}
function isHttpsProtocol(parsed) {
    return parsed.protocol === 'https:';
}
function matchesAllowListEntry(host, entry) {
    if (entry.startsWith('.')) {
        // Suffix match: host must end with the entry, ensuring the `.` separator is present.
        // This prevents "ayeldo.com.evil.com" from matching ".ayeldo.com".
        return host === entry.slice(1) || host.endsWith(entry);
    }
    // Exact match for bare hostnames like "localhost"
    return host === entry;
}
function isHostInAllowList(host, allowedHosts) {
    for (const entry of allowedHosts) {
        if (matchesAllowListEntry(host, entry)) {
            return true;
        }
    }
    return false;
}
export function isSafeRedirectTarget(target) {
    if (isEmptyTarget(target)) {
        return false;
    }
    if (isAbsoluteOrProtocolRelative(target)) {
        return false;
    }
    return isSameOrigin(target);
}
export function hasSafeRedirectTarget(param) {
    return param !== null && isSafeRedirectTarget(param);
}
/**
 * Validates a server-supplied URL against an allow-list of trusted host suffixes, or accepts
 * safe same-origin relative paths, before the browser navigates to it via `window.location.href`.
 *
 * Threat model: the server is the primary authority for these URLs. This check is a
 * defense-in-depth layer that refuses to follow a URL to an untrusted host in case the server
 * is compromised or misconfigured, preventing open-redirect and phishing attacks.
 *
 * Rules for **safe same-origin relative paths** (the `isSafeRedirectTarget` path):
 * - The URL must start with `/` and NOT start with `//` (i.e., not protocol-relative).
 * - The URL must not contain a scheme (`javascript:`, `data:`, etc.).
 * - When resolved against the current origin, it must resolve to the same origin.
 * - When a safe relative path is detected, navigation resolves within the current SPA origin;
 *   no cross-origin redirect is possible.
 *
 * Rules for **absolute URLs** (the allow-list path, defends against cross-origin redirects):
 * - The URL must parse as a valid absolute URL (the `URL` constructor must not throw).
 * - The protocol must be `https:`.
 * - The host must either exactly equal an allow-list entry (for bare hostnames like `localhost`)
 *   OR the host must end with the entry when the entry starts with `.`
 *   (e.g. `.ayeldo.com` matches `studio.ayeldo.com` and `studio.dev.ayeldo.com`).
 * - The trailing-substring trap is avoided: `ayeldo.com.evil.com` does NOT match `.ayeldo.com`
 *   because `endsWith('.ayeldo.com')` requires the `.` separator, which `evil.com` does not have.
 *
 * @param url - The URL string to validate.
 * @param allowedHosts - Allow-listed host suffixes. Bare hostnames (e.g. `localhost`) match exactly;
 *   entries starting with `.` (e.g. `.ayeldo.com`) match the host and all subdomains.
 * @returns `true` if the URL is safe to navigate to; `false` otherwise.
 */
export function isSafeRedirectUrl(url, allowedHosts) {
    if (isEmptyTarget(url)) {
        return false;
    }
    // Accept safe same-origin relative paths. When the browser follows a relative path via
    // window.location.href, it resolves against the current origin — no cross-origin redirect
    // is possible. isSafeRedirectTarget rejects protocol-relative URLs (//evil.com), URLs with
    // a scheme (javascript:, data:, https://...), and empty strings.
    if (isSafeRedirectTarget(url)) {
        return true;
    }
    const parsed = parseAbsoluteUrl(url);
    if (parsed === undefined) {
        return false;
    }
    if (!isHttpsProtocol(parsed)) {
        return false;
    }
    return isHostInAllowList(parsed.host, allowedHosts);
}
